import { useLayoutEffect, useRef } from "react";
import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { ProductSearchRequest } from "reedy-data/api";
import { useDebounce } from "~/hooks/use-debounce";
import { useDeepCompareMemo } from "use-deep-compare";
import { useSession } from "~/contexts/auth-context";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { logError } from "~/shared/services/error-reporting";

import { useApi } from "~/hooks/use-api";
import {
  buildElasticsearchRequest,
  mapElasticsearchResponse,
} from "./adapters";
import { SearchQuery, SearchResult } from "./types";

type UseSearchResult = UseQueryResult<SearchResult> & {
  requestParams: ProductSearchRequest;
};

export const useSearch = <Q extends Partial<SearchQuery>>(
  query: Q,
  options?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
    debounce?: number;
    includeAgeRangeBounds?: boolean;
    onSettled?(result: SearchResult | undefined, snapshot: Q): void;
  },
): UseSearchResult => {
  const api = useApi();
  const { organisation } = useSession();
  const { features } = useFeatureFlags();

  const includeAgeRangeBounds = Boolean(options?.includeAgeRangeBounds);

  const request = useDeepCompareMemo(
    () =>
      buildElasticsearchRequest(query, {
        hybridSearchEnabled: features.useElasticsearchHybridSearch,
        includeAgeRangeBounds,
      }),
    [query, features.useElasticsearchHybridSearch, includeAgeRangeBounds],
  );

  const params = useDebounce(request, options?.debounce ?? 100);

  const organisationId = organisation.id;
  const enabled = options?.enabled ?? true;
  // A fetch already in flight runs the queryFn of the observer that started
  // it, so a search that reports settles must not share its cache entry with
  // one that does not (e.g. the filter preview), or its settle is lost.
  const reportsSettle = Boolean(options?.onSettled);

  // What a settling response must still match to count as current. Org belongs
  // here next to the query: switching org leaves `query` untouched, so without
  // it an in-flight org-A response would settle into org-B's analytics.
  const currentRef = useRef({ query, organisationId });
  const onSettledRef = useRef(options?.onSettled);

  useLayoutEffect(() => {
    currentRef.current = { query, organisationId };
  }, [query, organisationId]);

  useLayoutEffect(() => {
    onSettledRef.current = options?.onSettled;
  }, [options?.onSettled]);

  const result = useQuery<SearchResult>({
    queryKey: ["search", params, organisationId, reportsSettle] as const,
    queryFn: async () => {
      const snapshot = query;
      const snapshotOrganisationId = organisationId;

      const notifyIfCurrent = (settleResult: SearchResult | undefined) => {
        const { current } = currentRef;
        if (
          isEqual(snapshot, current.query) &&
          snapshotOrganisationId === current.organisationId
        ) {
          try {
            onSettledRef.current?.(settleResult, snapshot);
          } catch (settledError) {
            // A throwing callback (analytics) must not fail a search that
            // succeeded, nor replace the real error on the failure path.
            logError(settledError);
          }
        }
      };

      try {
        const response = await api.products.searchProducts(params);
        const mapped = mapElasticsearchResponse(response ?? undefined);
        notifyIfCurrent(mapped);
        return mapped;
      } catch (error) {
        notifyIfCurrent(undefined);
        throw error;
      }
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus || false,
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });

  return {
    ...result,
    requestParams: params,
  };
};
